import React, { useState } from 'react';
import {
  IconButton,
  Avatar,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Typography,
  Box,
  Divider,
} from '@mui/material';
import {
  AccountCircle,
  Logout,
  GetApp,
  AdminPanelSettings,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '@/hooks/redux';
import { logout } from '@/store/slices/authSlice';
import timezoneService from '@/services/timezoneService';
import { logger } from '@/utils/logger';
import { getAvatarSrc, getInitials } from '@/utils/avatar';
import ProfileDialog from '@/components/user/ProfileDialog';
import { usePWAInstall } from '@/components/common/PWAInstallPrompt';

const UserMenu: React.FC = () => {
  const { t } = useTranslation(['common'], { useSuspense: false });
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { user } = useAppSelector(state => state.auth);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [profileOpen, setProfileOpen] = useState(false);
  const { canInstall, promptInstall } = usePWAInstall();

  const handleLogout = async () => {
    try {
      await dispatch(logout());
      navigate('/login');
    } catch (error) {
      logger.error('Logout failed', error);
    }
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const displayName = user?.display_name || user?.name || user?.email || '';
  const photoUrl = getAvatarSrc(user?.photo_url);
  const initials = getInitials(user?.display_name, user?.name, user?.email);

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          cursor: 'pointer',
          px: 1,
          py: 0.5,
          borderRadius: 1,
          '&:hover': { bgcolor: 'action.hover' },
        }}
        onClick={(e) => setAnchorEl(e.currentTarget)}
      >
        <Avatar
          src={photoUrl}
          sx={{ width: 32, height: 32, bgcolor: 'primary.main', fontSize: '0.875rem' }}
        >
          {initials || <AccountCircle />}
        </Avatar>
        <Typography
          variant="body2"
          sx={{ display: { xs: 'none', md: 'block' }, fontWeight: 500 }}
        >
          {displayName}
        </Typography>
      </Box>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        onClick={handleMenuClose}
        PaperProps={{
          elevation: 0,
          sx: {
            overflow: 'visible',
            filter: 'drop-shadow(0px 1px 2px rgba(0,0,0,0.2))',
            mt: 1.5,
            minWidth: 200,
            '&:before': {
              content: '""',
              display: 'block',
              position: 'absolute',
              top: 0,
              right: 14,
              width: 10,
              height: 10,
              bgcolor: 'background.paper',
              transform: 'translateY(-50%) rotate(45deg)',
              zIndex: 0,
            },
          },
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <MenuItem onClick={() => { setProfileOpen(true); handleMenuClose(); }}>
          <ListItemIcon>
            <AccountCircle />
          </ListItemIcon>
          <ListItemText>{t('navigation.profile')}</ListItemText>
        </MenuItem>
        <Box sx={{ px: 2, py: 1 }}>
          <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
            {t('system.timezone')}
          </Typography>
          <Typography variant="body2" fontWeight="medium">
            {timezoneService.getTimezoneDisplayName()}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {timezoneService.getUtcOffsetString()} {t('system.offset')}
          </Typography>
        </Box>
        <Divider />
        <MenuItem onClick={() => handleMenuClose()}>
          <Avatar src={photoUrl} sx={{ width: 24, height: 24, mr: 1 }}>
            {initials}
          </Avatar>
          <Box>
            <Typography variant="body2" noWrap>
              {user?.display_name || user?.name || user?.email}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {user?.email}
            </Typography>
          </Box>
        </MenuItem>
        {user?.role === 'admin' && (
          <MenuItem onClick={() => { navigate('/admin/dashboard'); handleMenuClose(); }}>
            <ListItemIcon>
              <AdminPanelSettings />
            </ListItemIcon>
            <ListItemText>{t('navigation.adminDashboard', 'Admin Dashboard')}</ListItemText>
          </MenuItem>
        )}
        {canInstall && (
          <MenuItem onClick={() => { promptInstall(); handleMenuClose(); }}>
            <ListItemIcon>
              <GetApp />
            </ListItemIcon>
            <ListItemText>{t('pwa.install', 'Install App')}</ListItemText>
          </MenuItem>
        )}
        <MenuItem onClick={handleLogout}>
          <ListItemIcon>
            <Logout />
          </ListItemIcon>
          <ListItemText>{t('navigation.logout')}</ListItemText>
        </MenuItem>
      </Menu>

      <ProfileDialog open={profileOpen} onClose={() => setProfileOpen(false)} />
    </>
  );
};

export default UserMenu;
